import { useEffect, useState, useCallback, useMemo } from 'react';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import moment from 'moment';
import { Card, Space } from 'antd';
import PageHeader from '../../uiKitComponents/pageHeader';
import Table from '../../uiKitComponents/table';
import Form from '../../uiKitComponents/form';
import DatePicker from '../../uiKitComponents/datePicker';
import Select from '../../uiKitComponents/select';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import PaginationSummary from '../../components/PaginationSummary';
import * as reports from '../../store/actions/reportPagesAction';
import {
  selectCountriesOptions,
  selectUSStatesOptions,
} from '../../store/selectors/bootstrapSelector';

const { RangePicker } = DatePicker;

const getMomentDate = (date) => (date ? moment(date, 'YYYY-MM-DD') : null);

const initialState = {
  page: 1,
  dates: { from: null, to: null },
  country_codes: [],
  state_codes: [],
};

const columns = [
  {
    dataIndex: 'country',
    title: 'Country',
  },
  {
    dataIndex: 'user_premium_count',
    title: 'Premium Users',
    sortDirections: ['descend', 'ascend', 'descend'],
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.user_premium_count - b.user_premium_count,
  },
  {
    dataIndex: 'user_non_premium_count',
    title: 'Non Premium Users',
    sortDirections: ['descend', 'ascend', 'descend'],
    sorter: (a, b) => a.user_non_premium_count - b.user_non_premium_count,
  },
  {
    dataIndex: 'user_fremium_count',
    title: 'Freemium Users',
    sortDirections: ['descend', 'ascend', 'descend'],
    sorter: (a, b) => a.user_fremium_count - b.user_fremium_count,
  },
];

const PilotsCountryPage = () => {
  const [form] = Form.useForm();

  const [state, setState] = useSessionStorage('pilots-country', {
    ...initialState,
  });
  const { page, dates, country_codes, state_codes } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const { cleanOnUnmount, pilotsCountry, countries, USStates } = useSelector(
    (store) => ({
      cleanOnUnmount: store.reportPages.cleanOnUnmount,
      pilotsCountry: store.reportPages.payload.pilotsCountry,
      countries: selectCountriesOptions(store),
      USStates: selectUSStatesOptions(store),
    }),
  );

  const dispatch = useDispatch();

  useMount(() => {
    reports.setCleanOnUnmountTrue()(dispatch);
    form.setFieldsValue({
      country_codes,
      state_codes,
      range: [
        getMomentDate(dates?.from?.locale),
        getMomentDate(dates?.to?.locale),
      ],
    });
  });

  const fetchPadStats = useCallback(() => {
    setLoading(true);

    reports
      .getPilotsCountry({
        from: dates?.from?.string,
        to: dates?.to?.string,
        country_codes,
        state_codes,
      })(dispatch)
      .catch((err) => {
        if (err.response.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('pilots-country');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, dates, country_codes, state_codes]);

  useEffect(() => {
    fetchPadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPadStats]);

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('pilots-country');
      reports.cleanReports()(dispatch);
    }
  });

  const onTableChange = useCallback(
    (pagination, _, __, { action }) => {
      const newState = {};
      if (action === 'paginate') {
        newState.page = pagination.current;
      }
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  const pagination = useMemo(() => {
    const pageSize = 25;
    const total = pilotsCountry?.data?.length || 0;

    return {
      page,
      pageSize,
      total,
      from: (page - 1) * pageSize + 1,
      to: Math.min(page * pageSize, total),
    };
  }, [pilotsCountry, page]);

  const onFinish = (values) => {
    const { range, country_codes: codes, state_codes: states } = values;

    if (range && range.length && range[0]) {
      const date_from = dayjs(range[0].$d).format('DD-MM-YYYY');
      const date_to = dayjs(range[1].$d).format('DD-MM-YYYY');

      setState((prevState) => ({
        ...prevState,
        dates: {
          from: { string: date_from, locale: range[0] },
          to: { string: date_to, locale: range[1] },
        },
        country_codes: codes,
        state_codes: states,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        dates: initialState.dates,
        country_codes: codes,
        state_codes: states,
      }));
    }
  };

  const onReset = () => {
    form.resetFields();
    setState(initialState);
  };

  if (error)
    return (
      <PageHeader className="page-main-header" title="Pilot Country">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader className="page-main-header" title="Pilot Country">
        <Card className="header-card">
          <h3>Search</h3>
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            onReset={onReset}
            className="d-flex justify-content-between"
          >
            <Space style={{ minWidth: 610 }}>
              <Form.Item name="country_codes" label="Select Countries">
                <Select
                  mode="multiple"
                  placeholder="Choose..."
                  options={countries}
                  optionFilterProp="label"
                  style={{
                    minWidth: 300,
                  }}
                />
              </Form.Item>
              <Form.Item dependencies={['country_codes']} noStyle>
                {({ getFieldValue }) => {
                  const selectedCountries = getFieldValue('country_codes');

                  if (
                    selectedCountries?.length === 1 &&
                    selectedCountries.includes('US')
                  ) {
                    return (
                      <Form.Item name="state_codes" label="Select US States">
                        <Select
                          mode="multiple"
                          placeholder="Choose..."
                          options={USStates}
                          optionFilterProp="label"
                          style={{
                            minWidth: 300,
                          }}
                        />
                      </Form.Item>
                    );
                  }

                  return null;
                }}
              </Form.Item>
            </Space>
            <Form.Item name="range" label="Select Dates">
              <RangePicker
                format="DD/MM/YYYY"
                disabledDate={(date) => date.isAfter(moment())}
              />
            </Form.Item>
            <Form.Item style={{ marginTop: 'calc(23px + 0.5rem)' }}>
              <Space>
                <Button type="dashed" htmlType="reset">
                  Clear
                </Button>
                <Button type="primary" htmlType="submit">
                  Select
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </PageHeader>
      <div className="page-main-content">
        <PaginationSummary meta={pagination} />
        <Table
          columns={columns}
          rowKey="country"
          dataSource={pilotsCountry}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: pagination.pageSize,
            total: pagination.total,
            current: pagination.page,
          }}
        />
        <PaginationSummary meta={pagination} />
      </div>
    </div>
  );
};

export default PilotsCountryPage;
